import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { createColumnHelper } from '@tanstack/react-table';
import DataTable from '@/components/functional/DataTable/DataTable.tsx';
import LoaderWrapper from '@/components/ui/LoaderWrapper/LoaderWrapper.tsx';
import useAdminSessions, { type AdminSession } from '@/api/admin/useAdminSessions.ts';
import Routes from '@/router/Routes.ts';
import { cn } from '@/utils/classNames.ts';

type FilterTab = 'all' | 'attention';

function govStatus(score: number | null | undefined): { label: string; color: string } | null {
  if (score === null || score === undefined) return null;
  return score >= 8.0
    ? { label: 'Passed', color: '#00a96e' }
    : { label: 'Caution', color: '#ffbe00' };
}

const columnHelper = createColumnHelper<AdminSession>();

function buildColumns(onOpen: (id: string) => void) {
  return [
    columnHelper.accessor('issue_title', {
      header: () => 'Governance Event',
      cell: (info) => (
        <span className="font-dm font-bold text-sm text-[#2b3440]">{info.getValue() ?? '—'}</span>
      ),
    }),
    columnHelper.accessor('user_email', {
      header: () => 'Decision Participant',
      cell: (info) => <span className="font-dm text-sm text-[#2b3440]">{info.getValue() ?? '—'}</span>,
    }),
    columnHelper.accessor('organization_name', {
      header: () => 'Operating Entity',
      cell: (info) => <span className="font-dm text-sm text-[#2b3440]">{info.getValue() ?? '—'}</span>,
    }),
    columnHelper.accessor('primary_framework', {
      header: () => 'Framework',
      cell: (info) => <span className="font-dm text-sm text-[#2b3440]">{info.getValue() ?? '—'}</span>,
    }),
    columnHelper.accessor('governance_score', {
      header: () => 'Gov. Score',
      cell: (info) => {
        const score = info.getValue();
        return (
          <span className="font-dm font-bold text-sm text-[#2b3440]">
            {score !== null && score !== undefined ? score : '—'}
          </span>
        );
      },
    }),
    columnHelper.display({
      id: 'gov_status',
      header: () => 'Status',
      cell: (info) => {
        const status = govStatus(info.row.original.governance_score);
        if (!status) return <span className="font-dm text-sm text-[#2b3440]">—</span>;
        return (
          <span className="font-dm font-bold text-sm" style={{ color: status.color }}>
            {status.label}
          </span>
        );
      },
    }),
    columnHelper.accessor('created_at', {
      header: () => 'Date',
      cell: (info) => (
        <span className="font-dm text-sm text-[#2b3440]">{dayjs(info.getValue()).format('MMM D')}</span>
      ),
    }),
    columnHelper.display({
      id: 'action',
      header: () => 'Action',
      enableSorting: false,
      cell: (info) => (
        <button
          onClick={() => onOpen(info.row.original.id)}
          className="font-dm font-bold text-sm text-[#0f1b2a] underline hover:text-[#3D5585] transition-colors"
        >
          Review Record
        </button>
      ),
    }),
  ];
}

const AdminSessionsPage: FC = () => {
  const navigate = useNavigate();
  const { data: sessions = [], isLoading } = useAdminSessions();
  const [activeTab, setActiveTab] = useState<FilterTab>('all');

  const columns = useMemo(
    () => buildColumns((id) => navigate(Routes.ADMIN_SESSION_DETAIL.replace(':id', id))),
    [navigate],
  );

  const filtered = useMemo(() => {
    if (activeTab === 'attention') return sessions.filter((s) => s.has_pending_escalation);
    return sessions;
  }, [sessions, activeTab]);

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-center px-6 py-4 bg-[#f4f9ff] rounded-xl">
        <div className="flex flex-col gap-1">
          <h1 className="font-newsreader text-[28px] font-extrabold leading-[125%] text-[rgba(0,0,0,0.87)]">
            Governance Events
          </h1>
          <p className="font-dm text-[14px] font-normal leading-[150%] tracking-[0.15px] text-[rgba(0,0,0,0.6)]">
            Monitor all governed decision lifecycles and identify those requiring attention.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex gap-4">
          {(['all', 'attention'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={cn(
                'px-4 py-3 rounded-xl text-sm font-dm font-extrabold transition-colors',
                activeTab === tab
                  ? 'bg-[#0f1b2a] text-white'
                  : 'border border-[rgba(204,204,204,0.5)] text-[rgba(15,27,42,0.5)] hover:bg-[#f4f9ff]'
              )}
            >
              {tab === 'all' ? 'All Governance Events' : 'Requires Governance Review'}
            </button>
          ))}
        </div>

        {isLoading ? (
          <LoaderWrapper isLoading />
        ) : filtered.length === 0 ? (
          <p className="font-dm text-sm text-center text-[#2b3440]/50 py-10">
            {activeTab === 'attention' ? 'No governance events requiring review.' : 'No governance events found.'}
          </p>
        ) : (
          <DataTable
            data={filtered}
            columns={columns}
            zebra
            className="table-auto"
            wrapperClassName="w-full rounded-2xl border border-[rgba(204,204,204,0.5)] bg-base-100"
          />
        )}
      </div>

    </div>
  );
};

export default AdminSessionsPage;
